import { useCallback, useEffect, useRef, useState } from "react";
import Modal from "../ui/Modal.jsx";
import Skeleton from "../ui/Skeleton.jsx";
import Spinner from "../ui/Spinner.jsx";
import StatusBadge from "./StatusBadge.jsx";
import RoleBadge from "./RoleBadge.jsx";
import UserDetailProfileTab from "./UserDetailProfileTab.jsx";
import UserDetailProjectsTab from "./UserDetailProjectsTab.jsx";
import UserDetailActivityTab from "./UserDetailActivityTab.jsx";
import DeactivateUserPanel from "./DeactivateUserPanel.jsx";
import DeletePanel from "./DeletePanel.jsx";
import TransferOwnershipPanel from "./TransferOwnershipPanel.jsx";
import { useProductMe } from "../../hooks/useProductMe.js";
import { formatName } from "../../utils/adminUserFormat.js";

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "projects", label: "Projects" },
  { id: "activity", label: "Activity" },
];

const tabBtnStyle = (active) => ({
  padding: "8px 14px",
  fontSize: "13px",
  fontWeight: active ? 600 : 500,
  border: "none",
  borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
  background: "transparent",
  color: active ? "var(--accent)" : "var(--text-secondary)",
  cursor: "pointer",
  fontFamily: "inherit",
  marginBottom: "-1px",
});

export default function UserDetailModal({ userId, onClose, onUserChanged, returnFocusRef }) {
  const { me, refresh: refreshMe } = useProductMe();
  const closeButtonRef = useRef(null);
  const titleId = "user-detail-modal-title";

  const [panel, setPanel] = useState("detail");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [profileDirty, setProfileDirty] = useState(false);

  const actorId = me?.user?.id ? String(me.user.id) : "";
  const actorIsOwner = Boolean(me?.user?.is_owner);

  const loadUser = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}`, {
        credentials: "include",
      });
      if (res.status === 404) {
        setError("User not found.");
        setUser(null);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error || `Failed to load user (${res.status})`);
        setUser(null);
        return;
      }
      const data = await res.json();
      setUser(data);
    } catch {
      setError("Could not load user details.");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const requestClose = useCallback(() => {
    if (panel === "detail" && profileDirty) {
      const discard = window.confirm("You have unsaved changes. Discard?");
      if (!discard) return;
    }
    onClose?.();
  }, [panel, profileDirty, onClose]);

  function confirmDiscardIfDirty() {
    if (panel !== "detail") return true;
    if (!profileDirty) return true;
    return window.confirm("You have unsaved changes. Discard?");
  }

  function openPanel(next) {
    if (!confirmDiscardIfDirty()) return;
    setPanel(next);
  }

  function handleSaved(updated) {
    setUser(updated);
    setProfileDirty(false);
    onUserChanged?.();
  }

  function handleActionSuccess() {
    onUserChanged?.();
    onClose?.();
  }

  async function handleTransferSuccess() {
    await refreshMe();
    onUserChanged?.();
    onClose?.();
  }

  const showTransferOwnership =
    Boolean(user?.is_owner) && actorIsOwner && actorId && user?.id && actorId === String(user.id);

  return (
    <Modal
      open
      onClose={requestClose}
      titleId={titleId}
      returnFocusRef={returnFocusRef}
      closeButtonRef={closeButtonRef}
      size="lg"
    >
      <div style={{ display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
        <div
          style={{
            flexShrink: 0,
            padding: "20px 24px 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "16px",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h2 id={titleId} style={{ fontSize: "20px", fontWeight: 600, margin: "0 0 4px", minHeight: "28px" }}>
              {loading ? <Skeleton width={200} height={24} borderRadius={4} /> : formatName(user?.name)}
            </h2>
            {!loading && user ? (
              <>
                <p className="text-secondary" style={{ fontSize: "14px", margin: "0 0 10px", wordBreak: "break-all" }}>
                  {user.email}
                </p>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <StatusBadge status={user.status} />
                  <RoleBadge user={user} />
                </div>
              </>
            ) : null}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={requestClose}
            aria-label="Close"
            style={{
              flexShrink: 0,
              width: "36px",
              height: "36px",
              border: "1px solid var(--border-bright)",
              borderRadius: "4px",
              background: "var(--bg-elevated)",
              color: "var(--text-secondary)",
              fontSize: "20px",
              lineHeight: 1,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            ×
          </button>
        </div>

        {!loading && !error && user && panel === "detail" ? (
          <div
            style={{
              flexShrink: 0,
              padding: "0 24px",
              borderBottom: "1px solid var(--border)",
              marginTop: "16px",
            }}
          >
            <div style={{ display: "flex", gap: "4px" }}>
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  style={tabBtnStyle(activeTab === tab.id)}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div style={{ flex: 1, overflow: "auto", padding: "20px 24px 24px", minHeight: 0 }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "160px",
                padding: "24px 0",
              }}
            >
              <Spinner label="Loading user details" />
            </div>
          ) : null}

          {!loading && error ? (
            <div>
              <p
                role="alert"
                style={{
                  padding: "12px 14px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  background: "var(--critical-dim)",
                  color: "var(--critical)",
                  border: "1px solid var(--critical)",
                  marginBottom: "12px",
                }}
              >
                {error}
              </p>
              <button
                type="button"
                onClick={loadUser}
                style={{
                  padding: "8px 14px",
                  fontSize: "13px",
                  fontWeight: 600,
                  borderRadius: "4px",
                  border: "1px solid var(--border-bright)",
                  background: "var(--bg-elevated)",
                  color: "var(--text-primary)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Retry
              </button>
            </div>
          ) : null}

          {!loading && !error && user && panel === "detail" ? (
            <>
              {activeTab === "profile" ? (
                <UserDetailProfileTab
                  user={user}
                  actorId={actorId}
                  onDirtyChange={setProfileDirty}
                  onSaved={handleSaved}
                  dangerZone={{
                    status: String(user.status || "").toLowerCase(),
                    deactivateDisabled: Boolean(user.is_owner) || actorId === user.id,
                    deactivateTooltip: user.is_owner
                      ? "Owner is protected"
                      : actorId === user.id
                        ? "You cannot deactivate yourself"
                        : "",
                    deleteDisabled: Boolean(user.is_owner) || actorId === user.id,
                    deleteTooltip: user.is_owner
                      ? "Owner is protected"
                      : actorId === user.id
                        ? "You cannot delete yourself"
                        : "",
                    onDeactivate: () => openPanel("deactivate"),
                    onDelete: () => {
                      if (String(user.status || "").toLowerCase() === "deactivated") {
                        openPanel("delete");
                      }
                    },
                    showTransferOwnership,
                    onTransferOwnership: () => openPanel("transfer"),
                  }}
                />
              ) : null}
              {activeTab === "projects" ? <UserDetailProjectsTab projects={user.projects} /> : null}
              {activeTab === "activity" ? <UserDetailActivityTab /> : null}
            </>
          ) : null}

          {!loading && !error && user && panel === "deactivate" ? (
            <DeactivateUserPanel
              user={user}
              actorId={actorId}
              onCancel={() => setPanel("detail")}
              onDone={handleActionSuccess}
            />
          ) : null}

          {!loading && !error && user && panel === "delete" ? (
            <DeletePanel
              user={user}
              onCancel={() => setPanel("detail")}
              onDone={handleActionSuccess}
            />
          ) : null}

          {!loading && !error && user && panel === "transfer" ? (
            <TransferOwnershipPanel
              actorId={actorId}
              onCancel={() => setPanel("detail")}
              onDone={handleTransferSuccess}
            />
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
